import { Editor, Transforms, Element as SlateElement, Range } from "slate";
import { IMAGE_EXTENSIONS } from "../../constants/common";
import { ImageElement, LinkElement } from "./types";
import isUrl from "is-url";

/**
 * Adds the formatting passed to the "marked" section
 * @param {ReactEditor} editor Selected text
 * @param {string} formatting Assign formatting to selection
 */
export const toggleMark = (editor: any, formatting: string): void => {
    const marks = Editor.marks(editor);
    const isActive = marks ? marks[formatting] === true : false;

    if (isActive) Editor.removeMark(editor, formatting);
    else Editor.addMark(editor, formatting, true);
};

/**
 * Checks if selected text contains a link
 * @param {ReactEditor} editor Selected text
 * @returns {boolean} Is there a link
 */
export const isLinkActive = (editor: any): boolean => {
    const [link] = Editor.nodes(editor, {
        match: (n) =>
            !Editor.isEditor(n) &&
            SlateElement.isElement(n) &&
            n.type === "link",
    });

    return !!link;
};

/**
 * Checks if selected text contains a link
 * @param {ReactEditor} editor Selected text
 * @returns {boolean} images contains the extension
 */
export const isImageUrl = (url: any) => {
    if (!url) return false;
    if (!isUrl(url)) return false;
    const ext = new URL(url).pathname.split(".").pop()!;
    return IMAGE_EXTENSIONS.includes(ext);
};

/**
 * Remove link in selection
 * @param {ReactEditor} editor Selected text
 */
export const unWrapLink = (editor: any): void => {
    Transforms.unwrapNodes(editor, {
        match: (n) =>
            !Editor.isEditor(n) &&
            SlateElement.isElement(n) &&
            n.type === "link",
    });
};

/**
 * Add link to selection
 * @param {ReactEditor} editor Selected text
 * @param {string} url URL of link to add to text
 */
export const wrapLink = (editor: any, url: string): void => {
    if (isLinkActive(editor)) {
        unWrapLink(editor);
    }

    const { selection } = editor;
    const isCollapsed = selection && Range.isCollapsed(selection);
    const link: LinkElement = {
        type: "link",
        url,
        children: isCollapsed ? [{ text: url }] : [],
    };

    if (isCollapsed) {
        Transforms.insertNodes(editor, link);
    } else {
        Transforms.wrapNodes(editor, link, { split: true });
        Transforms.collapse(editor, { edge: "end" });
    }
};

/**
 * Add link to selection (but check if there is a selection to link to first)
 * @param {ReactEditor} editor Selected text
 * @param {string} url URL of link to add to text
 */
export const insertLink = (editor: any, url: string) => {
    if (editor.selection) {
        wrapLink(editor, url);
    }
};

/**
 * Add link to selection (but check if there is a selection to link to first)
 * @param {ReactEditor} editor Selected text
 * @param {string} url URL of link to add to text
 */
export const insertImage = (editor: any, url: any) => {
    const text = { text: "" };
    const image: ImageElement = { type: "image", url, children: [text] };
    Transforms.insertNodes(editor, image);
};
